import { useCallback, useState } from 'react';
import { useForm } from '@refinedev/antd';
import { HttpError } from '@refinedev/core';
import { Button, Form, Input, DatePicker } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useParams } from 'react-router';
import type { Conference } from '@viq-admin/types';
import { RequireCan } from '../../components/RequireCan';

/**
 * Edit a conference as a BARE form — no Refine <Edit> page chrome — so it can
 * render in place inside the shared DetailPage (mirrors CompanyEdit's fold).
 * Save does NOT navigate (`redirect: false`); success is reported via
 * `onSaved` so DetailPage flips back to read mode. The form derives its own
 * `dirty` (useForm exposes none) and reports it via `onDirtyChange`.
 * `warnWhenUnsavedChanges: false` — DetailPage owns the discard guard.
 *
 * All props are optional so the standalone edit test can render
 * <ConferenceEdit/>. `id` is passed explicitly for robustness (at
 * /conferences/:id the inferred Refine action is `show`, not `edit`); it
 * falls back to the route param (kept for the redirected `/conferences/:id/edit`
 * → `../` case, and as a defensive fallback more generally).
 *
 * Record dates are hydrated to Dayjs for the pickers; `endDate` must be on or
 * after `startDate`.
 */
interface UpdateConferenceInput {
  name?: string;
  location?: string | null;
  startDate?: string;
  endDate?: string;
}

interface FormShape {
  name: string;
  location?: string;
  startDate: Dayjs;
  endDate: Dayjs;
}

export interface ConferenceEditProps {
  id?: string;
  onSaved?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

export function ConferenceEdit({ id, onSaved, onDirtyChange }: ConferenceEditProps = {}) {
  const params = useParams<{ id: string }>();
  const recordId = id ?? params.id;
  const [, setDirty] = useState(false);

  const { formProps, saveButtonProps, query, onFinish } = useForm<
    Conference,
    HttpError,
    UpdateConferenceInput
  >({
    resource: 'conferences',
    action: 'edit',
    id: recordId,
    redirect: false,
    warnWhenUnsavedChanges: false,
    onMutationSuccess: () => {
      setDirtyOnce(false);
      onSaved?.();
    },
  });

  const setDirtyOnce = useCallback(
    (next: boolean) => {
      setDirty((prev) => {
        if (prev !== next) onDirtyChange?.(next);
        return next;
      });
    },
    [onDirtyChange],
  );

  // Hydrate date fields into Dayjs so Ant Design DatePicker accepts them.
  const record = query?.data?.data;
  const initialValues = record
    ? {
        ...record,
        startDate: dayjs(record.startDate),
        endDate: dayjs(record.endDate),
      }
    : undefined;

  return (
    <RequireCan I="manage" a="Conference">
      <Form
        {...formProps}
        layout="vertical"
        initialValues={initialValues}
        onValuesChange={() => setDirtyOnce(true)}
        onFinish={(values) => {
          // `values` are the raw Dayjs pickers; map to the string API body.
          const v = values as unknown as FormShape;
          onFinish({
            name: v.name,
            location: v.location ?? null,
            startDate: v.startDate.format('YYYY-MM-DD'),
            endDate: v.endDate.format('YYYY-MM-DD'),
          });
        }}
      >
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: 'Name is required' }]}
        >
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item label="Location" name="location">
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item
          label="Start date"
          name="startDate"
          rules={[{ required: true, message: 'Start date is required' }]}
        >
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          label="End date"
          name="endDate"
          dependencies={['startDate']}
          rules={[
            { required: true, message: 'End date is required' },
            ({ getFieldValue }) => ({
              validator(_, value: Dayjs | undefined) {
                const start = getFieldValue('startDate') as Dayjs | undefined;
                if (!value || !start) return Promise.resolve();
                return value.isBefore(start, 'day')
                  ? Promise.reject(
                      new Error('End date must be on or after start'),
                    )
                  : Promise.resolve();
              },
            }),
          ]}
        >
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item>
          {/*
            No htmlType="submit" here: saveButtonProps.onClick already calls
            form.submit() once. antd's Button defaults to htmlType="button",
            so a click drives exactly that one submit path — a submit button
            inside this <Form> would ALSO trigger the browser's native form
            submit, invoking rc-field-form's own onSubmit -> form.submit()
            a second time.
          */}
          <Button type="primary" {...saveButtonProps}>
            Save
          </Button>
        </Form.Item>
      </Form>
    </RequireCan>
  );
}
